import React, { useEffect, useRef, useState } from "react";
import { Form, Input, Button, Divider, Row, Col, Select, Alert, Spin, message } from "antd";
import { PlusOutlined, CloseOutlined } from "@ant-design/icons";
import Axios from "axios";
import BasicAuthForm from "./BasicAuthForm";
import AuthOForm from "./AuthOForm";
import { useDispatch } from "react-redux";
import { getResourcesAsync } from "../../../redux/resourceSlice";
// import { testConnectionConnection } from '../../api/connectRestAPI'
const { REACT_APP_CONNECTION_INFO_BASE_URL } = process.env;
const GraphQLForm = (props) => {
  const [apiName, setApiName] = useState("");
  const [apiNameError, setApiNameError] = useState(false);
  const [apiUrl, setApiUrl] = useState("");
  const [apiTestStatus, setApiTestStatus] = useState("");
  const [apiSaveStatus, setApiSaveStatus] = useState("");
  const [urlParams, setUrlParams] = useState([{ key: "", value: "" }]);
  const [headers, setHeaders] = useState([{ key: "", value: "" }]);
  const [bodyParams, setBodyParams] = useState([{ key: "", value: "" }]);
  const [authMethod, setauthMethod] = useState("none");
  const [httpMethod, setHttpMethod] = useState("get");
  const [authParams, setAuthParams] = useState(null);
  const [authFormValid, setAuthFormValid] = useState(true);
  const [apiUrlError, setApiUrlError] = useState(false);
  const [resourceNameValidation, setResourceNameValidation] = useState({
    status: "",
    message: "",
  });
  const [debounceTimer, setDebounceTimer] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const resourceNameRef = useRef(null);
  // Add states for tracking changes and initial values
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialValues, setInitialValues] = useState({});

  const [urlParamsErrors, setUrlParamsErrors] = useState([]);
  const [headersErrors, setHeadersErrors] = useState([]);
  const [bodyParamsErrors, setBodyParamsErrors] = useState([]);

  const [cursorPosition, setCursorPosition] = useState(null);

  const [form] = Form.useForm();

  const authOptions = [
    { id: 0, name: "None", value: "none" },
    { id: 1, name: "Basic Auth", value: "basic" },
    { id: 2, name: "Auth0", value: "auth0" },
  ];

  // Drop rows the user never filled in — the trailing blank row must not be shown as saved data
  const withoutEmptyPairs = (pairs) =>
    (Array.isArray(pairs) ? pairs : []).filter(
      (pair) => (pair.key || "").trim() !== "" || (pair.value || "").trim() !== "",
    );

  // Validate key-value pairs
  const validateKeyValuePairs = (pairs) => {
    return pairs.map((pair) => {
      const hasKey = pair.key.trim() !== "";
      const hasValue = pair.value.trim() !== "";

      if (hasKey && !hasValue) {
        return { field: "value", message: "Value is required" };
      }
      if (!hasKey && hasValue) {
        return { field: "key", message: "Key is required" };
      }
      return { field: null, message: "" };
    });
  };

  // Check if there are any incomplete key-value pairs
  const hasIncompleteKeyValuePairs = () => {
    const urlParamsIncomplete = urlParams.some(
      (pair) =>
        (pair.key.trim() !== "" && pair.value.trim() === "") || (pair.key.trim() === "" && pair.value.trim() !== ""),
    );

    const headersIncomplete = headers.some(
      (pair) =>
        (pair.key.trim() !== "" && pair.value.trim() === "") || (pair.key.trim() === "" && pair.value.trim() !== ""),
    );

    const bodyParamsIncomplete = bodyParams.some(
      (pair) =>
        (pair.key.trim() !== "" && pair.value.trim() === "") || (pair.key.trim() === "" && pair.value.trim() !== ""),
    );

    return urlParamsIncomplete || headersIncomplete || bodyParamsIncomplete;
  };

  const validateUrl = (url) => {
    if (!url.trim()) return false;
    try {
      const urlObj = new URL(url);
      return ["http:", "https:"].includes(urlObj.protocol);
    } catch {
      return false;
    }
  };

  const checkForChanges = React.useCallback(
    (currentValues) => {
      if (!isEditMode) {
        setHasChanges(false);
        return;
      }

      // Filter out empty key-value pairs for comparison
      const filterEmpty = (arr) => {
        if (!Array.isArray(arr)) return [];
        return arr.filter((item) => item.key !== "" && item.value !== "");
      };

      const changed =
        currentValues.apiName !== initialValues.apiName ||
        currentValues.apiUrl !== initialValues.apiUrl ||
        currentValues.httpMethod !== initialValues.httpMethod ||
        currentValues.authMethod !== initialValues.authMethod ||
        JSON.stringify(filterEmpty(currentValues.urlParams)) !== JSON.stringify(filterEmpty(initialValues.urlParams)) ||
        JSON.stringify(filterEmpty(currentValues.headers)) !== JSON.stringify(filterEmpty(initialValues.headers)) ||
        JSON.stringify(filterEmpty(currentValues.bodyParams)) !==
          JSON.stringify(filterEmpty(initialValues.bodyParams)) ||
        JSON.stringify(currentValues.authParams) !== JSON.stringify(initialValues.authParams);

      setHasChanges(changed);
    },
    [isEditMode, initialValues],
  );

  useEffect(() => {
    if (resourceNameRef.current && cursorPosition !== null) {
      resourceNameRef.current.input.setSelectionRange(cursorPosition, cursorPosition);
      resourceNameRef.current.focus();
    }
  }, [resourceNameValidation]);

  useEffect(() => {
    if (props.selectedResource && props.selectedResource.connectionData) {
      const resource = props.selectedResource;
      const connectionData = resource.connectionData;

      setIsEditMode(true);

      // Set all state values
      const name = resource.name || "";
      const url = connectionData.baseUrl || "";
      const method = connectionData.requestValues?.httpMethod || "get";
      const auth = connectionData.authInfo?.method || "none";
      const authParms = connectionData.authInfo?.parameters || null;

      setApiName(name);
      setApiUrl(url);
      setHttpMethod(method);
      setauthMethod(auth);
      setAuthParams(authParms);

      // Set URL parameters - DEEP COPY for state
      let urlParms = [{ key: "", value: "" }];
      let initialUrlParams = [];
      if (connectionData.requestValues?.urlParams && connectionData.requestValues.urlParams.length > 0) {
        // Deep copy for initial values
        initialUrlParams = JSON.parse(JSON.stringify(connectionData.requestValues.urlParams));
        // Deep copy for state with conditional empty row
        urlParms = props.isViewOnly
          ? withoutEmptyPairs(JSON.parse(JSON.stringify(connectionData.requestValues.urlParams)))
          : [...JSON.parse(JSON.stringify(connectionData.requestValues.urlParams)), { key: "", value: "" }];
      } else if (props.isViewOnly) {
        // If isViewOnly and no url params, set to []
        urlParms = [];
      }
      setUrlParams(urlParms);

      // Set headers - DEEP COPY for state
      let headersList = [{ key: "", value: "" }];
      let initialHeadersList = [];
      if (connectionData.requestValues?.requestHeaders && connectionData.requestValues.requestHeaders.length > 0) {
        // Deep copy for initial values
        initialHeadersList = JSON.parse(JSON.stringify(connectionData.requestValues.requestHeaders));
        // Deep copy for state with conditional empty row
        headersList = props.isViewOnly
          ? withoutEmptyPairs(JSON.parse(JSON.stringify(connectionData.requestValues.requestHeaders)))
          : [...JSON.parse(JSON.stringify(connectionData.requestValues.requestHeaders)), { key: "", value: "" }];
      } else if (props.isViewOnly) {
        // If isViewOnly and no url params, set to []
        headersList = [];
      }
      setHeaders(headersList);

      // Set body parameters - DEEP COPY for state
      let bodyParms = [{ key: "", value: "" }];
      let initialBodyParams = [];
      if (connectionData.requestValues?.bodyParams && connectionData.requestValues.bodyParams.length > 0) {
        // Deep copy for initial values
        initialBodyParams = JSON.parse(JSON.stringify(connectionData.requestValues.bodyParams));
        // Deep copy for state with conditional empty row
        bodyParms = props.isViewOnly
          ? withoutEmptyPairs(JSON.parse(JSON.stringify(connectionData.requestValues.bodyParams)))
          : [...JSON.parse(JSON.stringify(connectionData.requestValues.bodyParams)), { key: "", value: "" }];
      } else if (props.isViewOnly) {
        // If isViewOnly and no body params, set to []
        bodyParms = [];
      }
      setBodyParams(bodyParms);

      // Store initial values for comparison (DEEP COPIED, no empty rows)
      const initial = {
        apiName: name,
        apiUrl: url,
        httpMethod: method,
        authMethod: auth,
        authParams: authParms ? JSON.parse(JSON.stringify(authParms)) : null,
        urlParams: initialUrlParams,
        headers: initialHeadersList,
        bodyParams: initialBodyParams,
      };
      setInitialValues(initial);

      // Set form field values
      form.setFieldsValue({
        apinameName: name,
        baseURL: url,
        httpMethodName: method,
      });
    } else {
      setIsEditMode(false);
      setHasChanges(false);
    }
  }, [props.selectedResource, form, props.isViewOnly]);

  const addUrlParam = () => {
    const newParams = [...urlParams, { key: "", value: "" }];
    setUrlParams(newParams);
    setUrlParamsErrors([...urlParamsErrors, ""]);
    checkForChanges({ apiName, apiUrl, httpMethod, authMethod, authParams, urlParams: newParams, headers, bodyParams });
  };

  const removeUrlParam = (index) => {
    const params = [...urlParams];
    const newParams = params.filter((p, i) => index !== i);
    const newErrors = urlParamsErrors.filter((e, i) => index !== i);
    setUrlParams(newParams);
    setUrlParamsErrors(newErrors);
    checkForChanges({ apiName, apiUrl, httpMethod, authMethod, authParams, urlParams: newParams, headers, bodyParams });
  };

  const onChangeUrlParamKey = (event, index, key) => {
    const params = [...urlParams];
    params[index][key] = event.target.value;
    setUrlParams(params);

    // Validate on change
    if (formSubmitted) {
      const errors = validateKeyValuePairs(params);
      setUrlParamsErrors(errors);
    }

    checkForChanges({ apiName, apiUrl, httpMethod, authMethod, authParams, urlParams: params, headers, bodyParams });
  };

  const addHeader = () => {
    const newHeaders = [...headers, { key: "", value: "" }];
    setHeaders(newHeaders);
    setHeadersErrors([...headersErrors, ""]);
    checkForChanges({
      apiName,
      apiUrl,
      httpMethod,
      authMethod,
      authParams,
      urlParams,
      headers: newHeaders,
      bodyParams,
    });
  };

  const removeHeader = (index) => {
    const headersList = [...headers];
    const newHeaders = headersList.filter((p, i) => index !== i);
    const newErrors = headersErrors.filter((e, i) => index !== i);
    setHeaders(newHeaders);
    setHeadersErrors(newErrors);
    checkForChanges({
      apiName,
      apiUrl,
      httpMethod,
      authMethod,
      authParams,
      urlParams,
      headers: newHeaders,
      bodyParams,
    });
  };

  const onChangeHeaderKey = (event, index, key) => {
    const headersList = [...headers];
    headersList[index][key] = event.target.value;
    setHeaders(headersList);

    // Validate on change
    if (formSubmitted) {
      const errors = validateKeyValuePairs(headersList);
      setHeadersErrors(errors);
    }

    checkForChanges({
      apiName,
      apiUrl,
      httpMethod,
      authMethod,
      authParams,
      urlParams,
      headers: headersList,
      bodyParams,
    });
  };

  const addBodyParams = () => {
    const newBodyParams = [...bodyParams, { key: "", value: "" }];
    setBodyParams(newBodyParams);
    setBodyParamsErrors([...bodyParamsErrors, ""]);
    checkForChanges({
      apiName,
      apiUrl,
      httpMethod,
      authMethod,
      authParams,
      urlParams,
      headers,
      bodyParams: newBodyParams,
    });
  };

  const removeBodyParams = (index) => {
    const headersList = [...bodyParams];
    const newBodyParams = headersList.filter((p, i) => index !== i);
    const newErrors = bodyParamsErrors.filter((e, i) => index !== i);
    setBodyParams(newBodyParams);
    setBodyParamsErrors(newErrors);
    checkForChanges({
      apiName,
      apiUrl,
      httpMethod,
      authMethod,
      authParams,
      urlParams,
      headers,
      bodyParams: newBodyParams,
    });
  };

  const onChangeBodyParamsKey = (event, index, key) => {
    const bodyParamsList = [...bodyParams];
    bodyParamsList[index][key] = event.target.value;
    setBodyParams(bodyParamsList);

    // Validate on change
    if (formSubmitted) {
      const errors = validateKeyValuePairs(bodyParamsList);
      setBodyParamsErrors(errors);
    }

    checkForChanges({
      apiName,
      apiUrl,
      httpMethod,
      authMethod,
      authParams,
      urlParams,
      headers,
      bodyParams: bodyParamsList,
    });
  };

  const testConnection = async () => {
    setApiTestStatus("pending");
    setApiSaveStatus("");
    if (apiName === "") {
      setApiNameError(true);
    }

    let urlParamSring = "";
    if (urlParams.length > 0) {
      urlParamSring = urlParamSring + "?";
      for (let i = 0; i < urlParams.length; i++) {
        if (urlParams[i]["key"] !== "" && urlParams[i]["value"] !== "") {
          urlParamSring = urlParamSring + urlParams[i]["key"] + "=" + urlParams[i]["value"] + "&";
        }
      }
      urlParamSring = urlParamSring.slice(0, -1);
    }

    let headerObj = {};
    if (headers.length > 0) {
      for (let i = 0; i < headers.length; i++) {
        if (headers[i]["key"] !== "" && headers[i]["value"] !== "") {
          headerObj[headers[i]["key"]] = headers[i]["value"];
        }
      }
    }

    let bodyParamObj = {};
    if (authParams) {
      bodyParamObj = Object.assign({}, authParams);
    }
    if (bodyParams.length > 0) {
      for (let i = 0; i < bodyParams.length; i++) {
        if (bodyParams[i]["key"] !== "" && bodyParams[i]["value"] !== "") {
          bodyParamObj[bodyParams[i]["key"]] = bodyParams[i]["value"];
        }
      }
    }

    let sendUrl = apiUrl + urlParamSring;
    try {
      await Axios({
        method: httpMethod,
        url: sendUrl,
        data: bodyParamObj,
        headers: headerObj,
      })
        .then((response) => {
          setApiTestStatus("success");
        })
        .catch((error) => {
          setApiTestStatus("error");
        });
    } catch (error) {
      setApiTestStatus("error");
    }
  };

  const checkGroupNameDuplicate = async (name) => {
    if (!name || name.trim() === "") {
      setResourceNameValidation({ status: "", message: "" });
      return;
    }

    // Store cursor position BEFORE setting checking status
    if (resourceNameRef.current && resourceNameRef.current.input) {
      setCursorPosition(resourceNameRef.current.input.selectionStart);
    }

    setResourceNameValidation({ status: "checking", message: "Checking availability..." });
    try {
      const response = await fetch(`${REACT_APP_CONNECTION_INFO_BASE_URL}/check-resource-duplicate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify({
          name: name.trim(),
          id: props.selectedResource?.id || null, // For edit mode
        }),
      });

      const data = await response.json();

      // Update cursor position again before setting result
      if (resourceNameRef.current && resourceNameRef.current.input) {
        setCursorPosition(resourceNameRef.current.input.selectionStart);
      }

      if (data.isDuplicate) {
        setResourceNameValidation({
          status: "duplicate",
          message: data.message || "A Resource with this name already exists",
        });
      } else {
        setResourceNameValidation({
          status: "available",
          message: "Resource name is available",
        });
      }
    } catch (error) {
      setResourceNameValidation({
        status: "error",
        message: "Error checking resource name availability",
      });
    }
  };

  const dispatch = useDispatch();

  const handleSaveConnection = async () => {
    setFormSubmitted(true);
    setApiSaveStatus("");

    const urlParamsValidation = validateKeyValuePairs(urlParams);
    const headersValidation = validateKeyValuePairs(headers);
    const bodyParamsValidation = validateKeyValuePairs(bodyParams);

    setUrlParamsErrors(urlParamsValidation);
    setHeadersErrors(headersValidation);
    setBodyParamsErrors(bodyParamsValidation);

    // Prevent submission if any key-value validation error exists
    const hasKeyValueErrors =
      urlParamsValidation.some((e) => e.field) ||
      headersValidation.some((e) => e.field) ||
      bodyParamsValidation.some((e) => e.field);

    if (hasKeyValueErrors) {
      setFormSubmitted(true);
      return;
    }
    if (apiName === "" || apiUrl === "" || !validateUrl(apiUrl)) {
      if (apiUrl === "" || !validateUrl(apiUrl)) {
        setApiUrlError(true);
      }
      return;
    } else {
      setApiUrlError(false);
    }

    if (authMethod !== "none" && !authFormValid) {
      // setApiSaveStatus("error");
      return;
    }

    if (resourceNameValidation.status === "duplicate") {
      message.destroy();
      message.error("Please resolve resource name duplicate issue before saving");
      return;
    }

    const url = REACT_APP_CONNECTION_INFO_BASE_URL;
    const JWT_TOKEN = `Bearer ${localStorage.getItem("accessToken")}`;
    setApiTestStatus("");
    setApiSaveStatus("pending");

    let postData = {
      name: apiName,
      icon: props.resourceType.icon,
      category: props.resourceType.category,
      description: props.resourceType.description,
      isActive: true,
      resoueceType: "GraphQL",
      connectionData: {
        name: apiName,
        baseUrl: apiUrl,
        authInfo: {
          method: authMethod,
          parameters: authParams ? authParams : {},
        },
        requestValues: {
          httpMethod: httpMethod,
          urlParams: urlParams.filter((qp) => (qp.key || "").trim() !== "" && (qp.value || "").trim() !== ""),
          requestHeaders: headers.filter((hp) => (hp.key || "").trim() !== "" && (hp.value || "").trim() !== ""),
          bodyParams: bodyParams.filter((bp) => (bp.key || "").trim() !== "" && (bp.value || "").trim() !== ""),
        },
      },
    };

    try {
      if (isEditMode && props.selectedResource?.id) {
        // UPDATE existing resource
        postData.id = props.selectedResource.id; // Include the ID for update
        const UPDATE_URL = `${url}/update-resource`;

        await Axios.post(UPDATE_URL, postData, {
          headers: { Authorization: JWT_TOKEN },
        });
      } else {
        // CREATE new resource
        const API_INFO_URL = `${url}/resource`;

        await Axios.post(API_INFO_URL, postData, {
          headers: { Authorization: JWT_TOKEN },
        });
      }

      setApiSaveStatus("success");
      if (props.clearSearch) {
        props.clearSearch();
      }
      setTimeout(() => {
        props.handleClose();
        dispatch(getResourcesAsync());
      }, 1500);
    } catch (error) {
      setApiSaveStatus("error");
    }
  };

  return (
    <div style={{}}>
      <Form
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
        name="basic"
        form={form}
        initialValues={{
          httpMethodName: "get",
        }}
      >
        <Form.Item
          style={{ marginBottom: "8px" }}
          label="Name"
          name="apinameName"
          rules={[{ required: true, message: "Please input name" }]}
          validateStatus={
            apiName.length > 0 && apiName.length < 2
              ? "error"
              : resourceNameValidation.status === "duplicate" || (formSubmitted && apiName === "")
                ? "error"
                : resourceNameValidation.status === "available"
                  ? "success"
                  : ""
          }
          help={
            apiName.length > 0 && apiName.length < 2 ? (
              "Name must be at least 2 characters long!"
            ) : resourceNameValidation.status === "duplicate" ? (
              resourceNameValidation.message
            ) : resourceNameValidation.status === "available" ? (
              <span style={{ color: "#52c41a" }}>{resourceNameValidation.message}</span>
            ) : formSubmitted && apiName === "" ? (
              "Please input Name"
            ) : (
              ""
            )
          }
        >
          <Input
            value={apiName}
            ref={resourceNameRef}
            onChange={(e) => {
              const newName = e.target.value;
              const currentCursor = e.target.selectionStart;
              setCursorPosition(currentCursor);

              setApiName(newName);
              setApiNameError(false);
              checkForChanges({
                apiName: newName,
                apiUrl,
                httpMethod,
                authMethod,
                authParams,
                urlParams,
                headers,
                bodyParams,
              });

              if (debounceTimer) {
                clearTimeout(debounceTimer);
              }

              // Set new timer for debounced API call
              if (newName.trim().length >= 2) {
                const newTimer = setTimeout(() => {
                  checkGroupNameDuplicate(newName);
                }, 800);

                setDebounceTimer(newTimer);
              }
            }}
            onKeyDown={(e) => {
              // Save cursor position on every keypress
              setCursorPosition(e.target.selectionStart);
            }}
            onClick={(e) => {
              // Save cursor position on click
              setCursorPosition(e.target.selectionStart);
            }}
            disabled={props.isViewOnly}
            suffix={
              resourceNameValidation.status === "checking" ? (
                <Spin size="small" />
              ) : resourceNameValidation.status === "available" ? (
                <span style={{ color: "#52c41a", fontSize: "16px" }}>✓</span>
              ) : resourceNameValidation.status === "duplicate" ? (
                <span style={{ color: "#ff4d4f", fontSize: "16px" }}>⚠</span>
              ) : null
            }
          />
        </Form.Item>

        <Divider />

        <Form.Item
          style={{ marginBottom: "8px" }}
          label="Base URL"
          name="baseURL"
          rules={[{ required: true, message: "Please input Base URL" }]}
          validateStatus={apiUrlError ? "error" : ""}
          help={apiUrlError ? "Please enter a Base URL (must start with http:// or https://)" : ""}
        >
          <Input
            value={apiUrl}
            onChange={(e) => {
              const newUrl = e.target.value;
              setApiUrl(newUrl);
              setApiUrlError(false);
              checkForChanges({
                apiName,
                apiUrl: newUrl,
                httpMethod,
                authMethod,
                authParams,
                urlParams,
                headers,
                bodyParams,
              });
            }}
            disabled={props.isViewOnly}
          />
        </Form.Item>

        <div style={{ marginTop: "16px" }}>
          {urlParams.map((urlParam, index) => (
            <Row key={"url_param_" + index} gutter={2} style={{ marginBottom: "8px" }}>
              <Col span={6} style={{ textAlign: "right", paddingRight: "8px" }}>
                {index === 0 ? (
                  <span>
                    URL Parameters
                    <span style={{ margin: "0 2px 0 2px" }}>:</span>
                  </span>
                ) : (
                  ""
                )}
              </Col>
              <Col span={18}>
                <Input.Group compact style={{ display: "flex", gap: "5px" }}>
                  <div style={{ width: "40%", display: "inline-block" }}>
                    <Input
                      style={{
                        width: "100%",
                        marginLeft: "-5px",
                        borderColor: urlParamsErrors[index]?.field === "key" ? "#ff4d4f" : undefined,
                      }}
                      s
                      placeholder="Key"
                      value={urlParams[index].key}
                      onChange={(e) => {
                        onChangeUrlParamKey(e, index, "key");
                      }}
                      disabled={props.isViewOnly}
                    />
                    {urlParamsErrors[index]?.field === "key" && (
                      <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>
                        {urlParamsErrors[index].message}
                      </div>
                    )}
                  </div>
                  <div style={{ width: "60%", display: "inline-block" }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Input
                        style={{
                          width: "100%",
                          borderColor: urlParamsErrors[index]?.field === "value" ? "#ff4d4f" : undefined,
                        }}
                        placeholder="Value"
                        value={urlParams[index].value}
                        onChange={(e) => {
                          onChangeUrlParamKey(e, index, "value");
                        }}
                        status={urlParamsErrors[index]?.field === "value" ? "error" : ""}
                        disabled={props.isViewOnly}
                      />
                      {!props.isViewOnly && (
                        <span
                          style={{
                            color: "#333",
                            cursor: "pointer",
                            display: "flex",
                            marginLeft: "5px",
                            alignItems: "center",
                            background: "#f0f0f0",
                            padding: "8px 10px 8px 10px",
                            borderRadius: "0 2px 2px 0",
                            borderTop: "1px solid #d9d9d9",
                            borderRight: "1px solid #d9d9d9",
                            borderBottom: "1px solid #d9d9d9",
                            border: "1px solid #d9d9d9",
                          }}
                          onClick={() => removeUrlParam(index)}
                        >
                          <CloseOutlined />
                        </span>
                      )}
                    </div>

                    {urlParamsErrors[index]?.field === "value" && (
                      <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>
                        {urlParamsErrors[index].message}
                      </div>
                    )}
                  </div>
                </Input.Group>
                {!props.isViewOnly && index === urlParams.length - 1 ? (
                  <Button
                    type="dashed"
                    onClick={() => addUrlParam()}
                    block
                    icon={<PlusOutlined />}
                    style={{ marginTop: "8px" }}
                  >
                    Add new
                  </Button>
                ) : null}
              </Col>
            </Row>
          ))}
        </div>

        <div style={{ marginTop: "16px" }}>
          {headers.map((header, index) => (
            <Row key={"header_" + index} style={{ marginBottom: "8px" }}>
              <Col span={6} style={{ textAlign: "right", paddingRight: "8px" }}>
                {index === 0 ? (
                  <span>
                    Headers
                    <span style={{ margin: "0 2px 0 2px" }}>:</span>
                  </span>
                ) : (
                  ""
                )}
              </Col>
              <Col span={18}>
                <Input.Group compact style={{ display: "flex", gap: "5px" }}>
                  <div style={{ width: "40%", display: "inline-block" }}>
                    <Input
                      style={{
                        width: "100%",
                        marginLeft: "-5px",
                        borderColor: headersErrors[index]?.field === "key" ? "#ff4d4f" : undefined,
                      }}
                      placeholder="Key"
                      value={headers[index].key}
                      onChange={(e) => {
                        onChangeHeaderKey(e, index, "key");
                      }}
                      disabled={props.isViewOnly}
                    />
                    {headersErrors[index]?.field === "key" && (
                      <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>
                        {headersErrors[index].message}
                      </div>
                    )}
                  </div>
                  <div style={{ width: "60%", display: "inline-block" }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Input
                        style={{
                          width: "100%",
                          borderColor: headersErrors[index]?.field === "value" ? "#ff4d4f" : undefined,
                        }}
                        placeholder="Value"
                        value={headers[index].value}
                        onChange={(e) => {
                          onChangeHeaderKey(e, index, "value");
                        }}
                        status={headersErrors[index]?.field === "value" ? "error" : ""}
                        disabled={props.isViewOnly}
                      />
                      {!props.isViewOnly && (
                        <span
                          style={{
                            color: "#333",
                            cursor: "pointer",
                            display: "flex",
                            marginLeft: "5px",
                            alignItems: "center",
                            background: "#f0f0f0",
                            padding: "8px 10px 8px 10px",
                            borderRadius: "0 2px 2px 0",
                            borderTop: "1px solid #d9d9d9",
                            borderRight: "1px solid #d9d9d9",
                            borderBottom: "1px solid #d9d9d9",
                            border: "1px solid #d9d9d9",
                          }}
                          onClick={() => removeHeader(index)}
                        >
                          <CloseOutlined />
                        </span>
                      )}
                    </div>
                    {headersErrors[index]?.field === "value" && (
                      <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>
                        {headersErrors[index].message}
                      </div>
                    )}
                  </div>
                </Input.Group>
                {!props.isViewOnly && index === headers.length - 1 ? (
                  <Button
                    type="dashed"
                    onClick={() => addHeader()}
                    block
                    icon={<PlusOutlined />}
                    style={{ marginTop: "8px" }}
                  >
                    Add new
                  </Button>
                ) : null}
              </Col>
            </Row>
          ))}
        </div>

        <div style={{ marginTop: "16px" }}>
          <Form.Item
            style={{ marginBottom: "8px" }}
            label="HTTP Method"
            name="httpMethodName"
            rules={[{ required: true, message: "Please select HTTP Method" }]}
            // validateStatus={apiNameError ? "error" : ""}
          >
            <Select
              onChange={(e) => {
                setHttpMethod(e);
                checkForChanges({
                  apiName,
                  apiUrl,
                  httpMethod: e,
                  authMethod,
                  authParams,
                  urlParams,
                  headers,
                  bodyParams,
                });
              }}
              value={httpMethod}
              defaultValue="get"
              disabled={props.isViewOnly}
            >
              <Select.Option value="get">GET</Select.Option>
              <Select.Option value="post">POST</Select.Option>
            </Select>
          </Form.Item>
        </div>

        {httpMethod === "post" ? (
          <div style={{ marginTop: "16px" }}>
            {bodyParams.map((header, index) => (
              <Row key={"bodyParam_" + index}>
                <Col style={{ textAlign: "right", paddingRight: "8px" }} span={6}>
                  {index === 0 ? (
                    <span>
                      Extra Body Values <span style={{ margin: "0 2px 0 2px" }}>:</span>
                    </span>
                  ) : (
                    ""
                  )}
                </Col>
                <Col span={18}>
                  <Input.Group compact style={{ display: "flex", gap: "5px" }}>
                    <div style={{ width: "40%", display: "inline-block" }}>
                      <Input
                        style={{
                          width: "100%",
                          marginLeft: "-5px",
                          borderColor: bodyParamsErrors[index]?.field === "key" ? "#ff4d4f" : undefined,
                        }}
                        placeholder="Key"
                        value={bodyParams[index].key}
                        onChange={(e) => {
                          onChangeBodyParamsKey(e, index, "key");
                        }}
                        disabled={props.isViewOnly}
                      />
                      {bodyParamsErrors[index]?.field === "key" && (
                        <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>
                          {bodyParamsErrors[index].message}
                        </div>
                      )}
                    </div>
                    <div style={{ width: "60%", display: "inline-block" }}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <Input
                          style={{
                            width: "100%",
                            borderColor: bodyParamsErrors[index]?.field === "value" ? "#ff4d4f" : undefined,
                          }}
                          placeholder="Value"
                          value={bodyParams[index].value}
                          onChange={(e) => {
                            onChangeBodyParamsKey(e, index, "value");
                          }}
                          status={bodyParamsErrors[index]?.field === "value" ? "error" : ""}
                          disabled={props.isViewOnly}
                        />
                        {!props.isViewOnly && (
                          <span
                            style={{
                              color: "#333",
                              cursor: "pointer",
                              display: "flex",
                              marginLeft: "5px",
                              alignItems: "center",
                              background: "#f0f0f0",
                              padding: "8px 10px 8px 10px",
                              borderRadius: "0 2px 2px 0",
                              borderTop: "1px solid #d9d9d9",
                              borderRight: "1px solid #d9d9d9",
                              borderBottom: "1px solid #d9d9d9",
                              border: "1px solid #d9d9d9",
                            }}
                            onClick={() => removeBodyParams(index)}
                          >
                            <CloseOutlined />
                          </span>
                        )}
                      </div>
                      {bodyParamsErrors[index]?.field === "value" && (
                        <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>
                          {bodyParamsErrors[index].message}
                        </div>
                      )}
                    </div>
                  </Input.Group>
                  {!props.isViewOnly && index === bodyParams.length - 1 ? (
                    <Button
                      type="dashed"
                      onClick={() => addBodyParams()}
                      block
                      icon={<PlusOutlined />}
                      style={{ marginTop: "8px" }}
                    >
                      Add new
                    </Button>
                  ) : null}
                </Col>
              </Row>
            ))}
          </div>
        ) : null}

        <div style={{ marginTop: "16px" }}>
          <Form.Item style={{ marginBottom: "8px" }} label="Authentication" validateFirst={authMethod}>
            <Select
              onChange={(e) => {
                setauthMethod(e);
                setAuthParams(null);
                checkForChanges({
                  apiName,
                  apiUrl,
                  httpMethod,
                  authMethod: e,
                  authParams: null,
                  urlParams,
                  headers,
                  bodyParams,
                });
              }}
              defaultValue="none"
              disabled={props.isViewOnly}
              value={authMethod}
            >
              {authOptions.map((opt) => {
                return (
                  <Select.Option key={"auth_opt_" + opt.id} value={opt.value}>
                    {opt.name}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
        </div>

        {(() => {
          switch (authMethod) {
            case "basic":
              return (
                <BasicAuthForm
                  handleChangeValues={(params) => {
                    setAuthParams(params);
                    checkForChanges({
                      apiName,
                      apiUrl,
                      httpMethod,
                      authMethod,
                      authParams: params,
                      urlParams,
                      headers,
                      bodyParams,
                    });
                  }}
                  isDisabled={props.isViewOnly}
                  resource={props.selectedResource}
                  connectionData={props?.selectedResource?.connectionData}
                  onValidationChange={setAuthFormValid}
                />
              );
            case "auth0":
              return (
                <AuthOForm
                  handleChangeValues={(params) => {
                    setAuthParams(params);
                    checkForChanges({
                      apiName,
                      apiUrl,
                      httpMethod,
                      authMethod,
                      authParams: params,
                      urlParams,
                      headers,
                      bodyParams,
                    });
                  }}
                  isViewOnly={props.isViewOnly}
                  connectionData={props?.selectedResource?.connectionData}
                  onValidationChange={setAuthFormValid}
                />
              );
            default:
              return <div></div>;
          }
        })()}
        {!props.isViewOnly && (
          <>
            <Divider />
            <div
              style={{
                display: "flex",
                flexDirection: "row-reverse",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <Button
                  htmlType="submit"
                  onClick={testConnection}
                  loading={apiTestStatus === "pending"}
                  disabled={apiUrl === "" || apiName === ""}
                >
                  Test Connection
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  onClick={handleSaveConnection}
                  loading={apiSaveStatus === "pending"}
                  disabled={(authMethod !== "none" && !authFormValid) || (isEditMode && !hasChanges)}
                >
                  {isEditMode ? "Update Resource" : "Create Resource"}
                </Button>
              </div>
              {/* <div>{apiTestStatus === "success" ? 
                        <Alert message="Test Connection Success" type="success" showIcon /> 
                        :apiTestStatus === "error" ? 
                            <Alert message="Test Connection Failed" type="error" showIcon /> 
                            : ''}
                        </div> */}
              {(() => {
                switch (apiTestStatus) {
                  case "success":
                    return <Alert message="Test Connection Success" type="success" showIcon />;
                  case "error":
                    return <Alert message="Test Connection Failed" type="error" showIcon />;
                  case "pending":
                    return (
                      <div>
                        <Spin />
                      </div>
                    );
                  default:
                    return <div></div>;
                }
              })()}

              {(() => {
                switch (apiSaveStatus) {
                  case "success":
                    return <Alert message="Saving API data Success" type="success" showIcon />;
                  case "error":
                    return <Alert message="Saving API data Failed" type="error" showIcon />;
                  case "pending":
                    return (
                      <div>
                        <Spin />
                      </div>
                    );
                  default:
                    return <div></div>;
                }
              })()}
            </div>
          </>
        )}
      </Form>
    </div>
  );
};

export default GraphQLForm;
